import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  DateRange,
  FilterList,
} from '@mui/icons-material';
import { getRequest } from '../../services/api';
import { STORAGE_KEYS } from '../../config/constants';
import moment from 'moment';

const Earnings = () => {
  const [currentTab, setCurrentTab] = useState(0); // 0: Services, 1: Contracts
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [serviceEarnings, setServiceEarnings] = useState([]);
  const [contractEarnings, setContractEarnings] = useState([]);

  const [totalServiceEarnings, setTotalServiceEarnings] = useState(0);
  const [totalContractEarnings, setTotalContractEarnings] = useState(0);

  // Filter states
  const [timePeriod, setTimePeriod] = useState('thisMonth');
  const [amountRange, setAmountRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const timePeriods = [
    { value: 'today', label: 'Today' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'lastWeek', label: 'Last Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'lastYear', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const amountRanges = [
    { value: 'all', label: 'All Amounts' },
    { value: '100-10000', label: '₹100 - ₹10,000' },
    { value: '10000-100000', label: '₹10,000 - ₹1 Lakh' },
    { value: '100000+', label: 'Above ₹1 Lakh' },
  ];

  useEffect(() => {
    loadEarnings();
  }, [timePeriod, customStartDate, customEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEarnings = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
      const userType = 'manager';

      let dateFilter = getDateRange(timePeriod);

      // Build query params like mobile app
      const startDate = timePeriod === 'custom' && customStartDate ? customStartDate : dateFilter.startDate.split('T')[0];
      const endDate = timePeriod === 'custom' && customEndDate ? customEndDate : dateFilter.endDate.split('T')[0];

      // API endpoint matching mobile app: earnings?userId={id}&userType={type}&filter={period}&startDate={date}&endDate={date}
      const uri = `earnings?userId=${userId}&userType=${userType}&filter=${timePeriod}&startDate=${startDate}&endDate=${endDate}`;

      console.log('Fetching earnings from:', uri);

      const response = await getRequest(uri);

      // Response structure from mobile app: { summary: {...}, services: [...], contracts: [...] }
      if (response) {
        const services = response.services || [];
        const contracts = response.contracts?.filter(contract=>contract?.commissionStatus==='completed') || [];
console.log('Fetched services:', services);
console.log('Fetched contracts:', contracts);
        setServiceEarnings(services);
        setContractEarnings(contracts);

        // Calculate totals (use totalAmount or amount field)
        const serviceTotal = services.reduce((sum, e) => sum + (parseFloat(e.totalAmount || e.amount) || 0), 0);
        const contractTotal = contracts.reduce((sum, e) => sum + (parseFloat(e.totalAmount || e.amount) || 0), 0);

        setTotalServiceEarnings(serviceTotal);
        setTotalContractEarnings(contractTotal);
      }
    } catch (error) {
      console.error('Error loading earnings:', error);
      setError('Failed to load earnings data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (period) => {
    const now = moment();
    let startDate, endDate;

    switch (period) {
      case 'today':
        startDate = now.startOf('day').toISOString();
        endDate = now.endOf('day').toISOString();
        break;
      case 'thisWeek':
        startDate = now.startOf('week').toISOString();
        endDate = now.endOf('week').toISOString();
        break;
      case 'lastWeek':
        startDate = now.subtract(1, 'week').startOf('week').toISOString();
        endDate = now.subtract(1, 'week').endOf('week').toISOString();
        break;
      case 'thisMonth':
        startDate = now.startOf('month').toISOString();
        endDate = now.endOf('month').toISOString();
        break;
      case 'lastMonth':
        startDate = now.subtract(1, 'month').startOf('month').toISOString();
        endDate = now.subtract(1, 'month').endOf('month').toISOString();
        break;
      case 'thisYear':
        startDate = now.startOf('year').toISOString();
        endDate = now.endOf('year').toISOString();
        break;
      case 'lastYear':
        startDate = now.subtract(1, 'year').startOf('year').toISOString();
        endDate = now.subtract(1, 'year').endOf('year').toISOString();
        break;
      default:
        startDate = now.startOf('month').toISOString();
        endDate = now.endOf('month').toISOString();
    }

    return { startDate, endDate };
  };

  const filterByAmount = (earnings) => {
    if (amountRange === 'all') return earnings;

    return earnings.filter(e => {
      const amount = parseFloat(e.amount);
      switch (amountRange) {
        case '100-10000':
          return amount >= 100 && amount <= 10000;
        case '10000-100000':
          return amount > 10000 && amount <= 100000;
        case '100000+':
          return amount > 100000;
        default:
          return true;
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    return moment(date).format('MMM DD, YYYY hh:mm A');
  };

  const currentEarnings = currentTab === 0 ? filterByAmount(serviceEarnings) : filterByAmount(contractEarnings);
  const currentTotal = currentTab === 0 ? totalServiceEarnings : totalContractEarnings;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Earnings Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Track your service and contract earnings
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderLeft: '4px solid #2196f3' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Service Earnings</Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {formatCurrency(totalServiceEarnings)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderLeft: '4px solid #4caf50' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Contract Earnings</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {formatCurrency(totalContractEarnings)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Total Earnings</Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {formatCurrency(totalServiceEarnings + totalContractEarnings)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <DateRange color="action" />
          <TextField
            select
            label="Time Period"
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            size="small"
            sx={{ minWidth: 150 }}
          >
            {timePeriods.map(period => (
              <MenuItem key={period.value} value={period.value}>
                {period.label}
              </MenuItem>
            ))}
          </TextField>

          {timePeriod === 'custom' && (
            <>
              <TextField
                type="date"
                label="Start Date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="End Date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          {currentTab === 1 && (
            <>
              <FilterList color="action" />
              <TextField
                select
                label="Amount Range"
                value={amountRange}
                onChange={(e) => setAmountRange(e.target.value)}
                size="small"
                sx={{ minWidth: 180 }}
              >
                {amountRanges.map(range => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          <Button
            variant="contained"
            startIcon={<TrendingUp />}
            onClick={loadEarnings}
          >
            Apply Filters
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper>
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
          <Tab label={`Service Earnings (${serviceEarnings.length})`} />
          <Tab label={`Contract Earnings (${contractEarnings.length})`} />
        </Tabs>

        {/* Earnings Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Booking ID</TableCell>
                <TableCell>Service/Category</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Pincode</TableCell>
                <TableCell>Partner</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentEarnings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">No earnings found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                currentEarnings.map((earning, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Chip label={earning.serviceRequest?.bookingId || 'N/A'} size="small" />
                    </TableCell>
                    <TableCell>{earning.serviceName || earning.categoryName || 'N/A'}</TableCell>
                    <TableCell>{earning.serviceRequest?.customerName || 'N/A'}</TableCell>
                    <TableCell>{earning.serviceRequest?.pincode || 'N/A'}</TableCell>
                    <TableCell>{earning?.partner?.name || 'N/A'}</TableCell>
                    <TableCell>{formatDate(earning.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {formatCurrency((earning?.contractId ? earning?.amount : earning?.totalAmount) || 0)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {currentEarnings.length > 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="right">
                    <Typography variant="subtitle1" fontWeight="bold">
                      Total:
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="h6" fontWeight="bold" color="success.main">
                      {formatCurrency(currentTotal)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Earnings;
